import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DAY_LABELS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const key = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const CATEGORY_COLORS = [
  "#65A30D", "#1F7A8C", "#B5651D", "#4C6EF5", "#2F9E44",
  "#9C36B5", "#E8590C", "#0C8599", "#7A6E00", "#5C7CFA",
];
const colorFor = (category) => {
  let hash = 0;
  for (const ch of category || "") hash = (hash * 31 + ch.charCodeAt(0)) % 9973;
  return CATEGORY_COLORS[hash % CATEGORY_COLORS.length];
};

const timeLabel = (iso) => {
  const d = new Date(iso);
  const h = d.getHours() % 12 || 12;
  const m = d.getMinutes();
  return `${h}${m ? `:${String(m).padStart(2, "0")}` : ""}${d.getHours() < 12 ? "am" : "pm"}`;
};

export const MonthGrid = ({ events }) => {
  const navigate = useNavigate();
  const firstEventDate = events.length ? new Date(events[0].start_at) : new Date();
  const [cursor, setCursor] = useState(new Date(firstEventDate.getFullYear(), firstEventDate.getMonth(), 1));
  const [expanded, setExpanded] = useState(null);

  const byDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      const k = e.start_at.slice(0, 10);
      (map[k] = map[k] || []).push(e);
    }
    Object.values(map).forEach((list) => list.sort((a, b) => a.start_at.localeCompare(b.start_at)));
    return map;
  }, [events]);

  const gridStart = useMemo(() => {
    const d = new Date(cursor);
    d.setDate(1 - d.getDay());
    return d;
  }, [cursor]);

  const cells = useMemo(
    () =>
      Array.from({ length: 42 }, (_, i) => {
        const d = new Date(gridStart);
        d.setDate(gridStart.getDate() + i);
        return d;
      }),
    [gridStart]
  );

  const todayKey = key(new Date());
  const move = (delta) => {
    setExpanded(null);
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
  };
  const goToday = () => {
    const n = new Date();
    setExpanded(null);
    setCursor(new Date(n.getFullYear(), n.getMonth(), 1));
  };

  return (
    <div data-testid="month-grid">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <button
          onClick={goToday}
          data-testid="cal-today"
          className="rounded-full border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
        >
          Today
        </button>
        <div className="flex gap-1">
          <button
            onClick={() => move(-1)}
            data-testid="cal-prev"
            className="grid h-9 w-9 place-items-center rounded-full transition-colors duration-200 hover:bg-neutral-100"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => move(1)}
            data-testid="cal-next"
            className="grid h-9 w-9 place-items-center rounded-full transition-colors duration-200 hover:bg-neutral-100"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <h3 className="text-xl font-extrabold tracking-tight text-neutral-900" data-testid="cal-month-label">
          {cursor.toLocaleString("en-US", { month: "long", year: "numeric" })}
        </h3>
      </div>

      <div className="hidden overflow-hidden rounded-2xl border border-neutral-200 bg-white sm:block">
        <div className="grid grid-cols-7 border-b border-neutral-200">
          {DAY_LABELS.map((d) => (
            <div key={d} className="py-2.5 text-center text-[11px] font-bold tracking-wide text-neutral-500">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {cells.map((date, i) => {
            const k = key(date);
            const dayEvents = byDay[k] || [];
            const inMonth = date.getMonth() === cursor.getMonth();
            const isToday = k === todayKey;
            const isExpanded = expanded === k;
            const visible = isExpanded ? dayEvents : dayEvents.slice(0, 3);

            return (
              <div
                key={k}
                data-testid={`cal-cell-${k}`}
                className={`min-h-[132px] border-b border-r border-neutral-200 p-1.5 ${
                  i % 7 === 6 ? "border-r-0" : ""
                } ${inMonth ? "bg-white" : "bg-neutral-50/60"}`}
              >
                <div className="mb-1 flex justify-center">
                  <span
                    className={`grid h-6 min-w-6 place-items-center rounded-full px-1.5 text-xs font-semibold ${
                      isToday
                        ? "bg-[#B8FF2E] text-[#14210A]"
                        : inMonth
                        ? "text-neutral-800"
                        : "text-neutral-400"
                    }`}
                  >
                    {date.getDate() === 1
                      ? date.toLocaleString("en-US", { month: "short", day: "numeric" })
                      : date.getDate()}
                  </span>
                </div>

                <div className="space-y-0.5">
                  {visible.map((e) => (
                    <button
                      key={e.event_id}
                      onClick={() => navigate(`/events/${e.event_id}`)}
                      data-testid={`cal-chip-${e.event_id}`}
                      title={`${timeLabel(e.start_at)} ${e.title} · ${e.venue_name}`}
                      className="flex w-full items-center gap-1.5 rounded px-1 py-[3px] text-left transition-colors duration-200 hover:bg-neutral-100"
                    >
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: colorFor(e.category) }}
                      />
                      <span className="truncate text-[11px] leading-tight text-neutral-700">
                        <span className="hidden font-semibold sm:inline">{timeLabel(e.start_at)} </span>
                        {e.title}
                      </span>
                    </button>
                  ))}

                  {dayEvents.length > 3 && (
                    <button
                      onClick={() => setExpanded(isExpanded ? null : k)}
                      data-testid={`cal-more-${k}`}
                      className="px-1 text-[11px] font-semibold text-neutral-500 transition-colors duration-200 hover:text-neutral-900"
                    >
                      {isExpanded ? "show less" : `${dayEvents.length - 3} more`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-5 sm:hidden" data-testid="month-agenda">
        {cells
          .filter((d) => d.getMonth() === cursor.getMonth() && (byDay[key(d)] || []).length > 0)
          .map((d) => (
            <div key={key(d)}>
              <p className="mb-2 text-sm font-bold text-neutral-900">
                {d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
              </p>
              <div className="space-y-2">
                {(byDay[key(d)] || []).map((e) => (
                  <button
                    key={e.event_id}
                    onClick={() => navigate(`/events/${e.event_id}`)}
                    data-testid={`agenda-chip-${e.event_id}`}
                    className="flex w-full items-center gap-3 rounded-xl border border-neutral-200 p-3 text-left transition-colors duration-200 active:bg-neutral-50"
                  >
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: colorFor(e.category) }}
                    />
                    <span className="w-16 shrink-0 text-xs font-bold text-neutral-700">{timeLabel(e.start_at)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-neutral-900">{e.title}</span>
                      <span className="block truncate text-xs text-neutral-500">
                        {e.venue_name} · {e.neighborhood}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
